import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import { differenceBy, pullAt, differenceWith } from "lodash";
import AutoSizer from "react-virtualized-auto-sizer";
import { VariableSizeList as List } from "react-window";
import { useTranslation } from "react-i18next";
import DualList from "../../components/DualList/DualList";
import Skeleton from "../Skeleton/Skeleton";
import LoadingButton from "../Buttons/LoadingButton";
import { PropTypes } from "prop-types";
import ButtonGroup from "../Buttons/ButtonGroup";

const isEqualById = (itemA, itemB) => itemA.id === itemB.id;

const DualListSkeleton = ({}) => {
  return (
    <Box display="flex" flex={1} minHeight={460}>
      <Skeleton loading flex={1} height="100%" mr={1.5}></Skeleton>
      <Skeleton loading flex={1} height="100%" ml={1.5}></Skeleton>
    </Box>
  );
};

const DualListContainer = ({
  initialSelectedItems,
  initialUnselectedItems,
  selectedListHeader,
  unselectedListHeader,
  filters,
  filterUnselected,
  filterSelected,
  rowHeight,
  estimatedItemSize,
  getListItemDetails,
  saveButtonText,
  onSave,
  isLoadingItems,
  disabled,
  isSaving,
  unselectedListAction,
  selectedListAction,
  onCancel,
  cancelButtonText
}) => {
  const { t } = useTranslation();
  const [unselectedItems, setUnselectedItems] = useState(
    initialUnselectedItems
  );
  const [hiddenUnselected, setHiddenUnselected] = useState([]);
  const [hiddenSelected, setHiddenSelected] = useState([]);
  const [selectedItems, setSelectedItems] = useState(initialSelectedItems);

  // If initialUnselectedItems is updated, the state of the hidden items and displayed unselected items will not change
  // but the items within each will be updated to the new items from initialUnselectedItems based on their ID
  useEffect(() => {
    const updatedHiddenUnselected = hiddenUnselected.filter(hiddenItem => {
      return initialUnselectedItems.length > 0
        ? initialUnselectedItems.find(item => isEqualById(item, hiddenItem))
        : false;
    });
    setHiddenUnselected(updatedHiddenUnselected);
    setUnselectedItems(
      differenceWith(initialUnselectedItems, hiddenUnselected, isEqualById)
    );
  }, [initialUnselectedItems]);

  useEffect(() => {
    const updatedHiddenSelected = hiddenSelected.map(hiddenItem =>
      initialSelectedItems.find(item => isEqualById(item, hiddenItem))
    );
    setHiddenSelected(updatedHiddenSelected);
    setSelectedItems(
      differenceWith(initialSelectedItems, hiddenSelected, isEqualById)
    );
  }, [initialSelectedItems]);

  useEffect(() => {
    const allItems = [...unselectedItems, ...hiddenUnselected];
    const displayedUnselected = !!filterUnselected
      ? filterUnselected(allItems)
      : allItems;
    const hiddenUnselectedList = differenceBy(allItems, displayedUnselected);
    setUnselectedItems(displayedUnselected);
    setHiddenUnselected(hiddenUnselectedList);
  }, [filterUnselected]);

  useEffect(() => {
    const allSelectedItems = [...selectedItems, ...hiddenSelected];
    const displayedSelected = !!filterSelected
      ? filterSelected(allSelectedItems)
      : allSelectedItems;
    const hiddenSelectedList = differenceBy(
      allSelectedItems,
      displayedSelected
    );
    setHiddenSelected(hiddenSelectedList);
    setSelectedItems(displayedSelected);
  }, [filterSelected]);

  const selectedList = selectedItems.map(getListItemDetails);

  const unselectedList = unselectedItems.map(getListItemDetails);

  const handleRemoveAll = () => {
    setUnselectedItems([...unselectedItems, ...selectedItems]);
    setSelectedItems([]);
  };

  const handleAddAll = () => {
    setSelectedItems([...selectedItems, ...unselectedItems]);
    setUnselectedItems([]);
  };

  const handleAddToSelectedList = (item, index) => {
    const newUnselectedItems = [...unselectedItems];
    const itemToMove = pullAt(newUnselectedItems, [index]);
    setSelectedItems([...selectedItems, ...itemToMove]);
    setUnselectedItems(newUnselectedItems);
  };

  const handleAddToUnselectedList = (item, index) => {
    const newSelectedItems = [...selectedItems];
    const itemToMove = pullAt(newSelectedItems, [index]);
    setUnselectedItems([...unselectedItems, ...itemToMove]);
    setSelectedItems(newSelectedItems);
  };

  const handleSave = () => {
    onSave(
      [...unselectedItems, ...hiddenUnselected],
      [...selectedItems, ...hiddenSelected]
    );
  };

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      {filters && <Box mb={3}>{filters}</Box>}
      {isLoadingItems ? (
        <DualListSkeleton />
      ) : (
        <DualList
          renderList={(items, DualListItem) => (
            <Box flex={1} minHeight={460} mt={3}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    className="List"
                    height={height}
                    itemCount={items.length}
                    itemSize={
                      rowHeight instanceof Function
                        ? index => rowHeight(items[index])
                        : () => rowHeight
                    }
                    width={width}
                    estimatedItemSize={estimatedItemSize}
                  >
                    {({ index, style }) => {
                      return (
                        <div style={style}>
                          {DualListItem(items[index], index)}
                        </div>
                      );
                    }}
                  </List>
                )}
              </AutoSizer>
            </Box>
          )}
          onAddAll={handleAddAll}
          removeAllButtonText={t("dualList.removeAllBtn")}
          onRemoveAll={handleRemoveAll}
          onAddToSelectedList={handleAddToSelectedList}
          onAddToUnselectedList={handleAddToUnselectedList}
          selectedListHeader={selectedListHeader}
          unselectedListHeader={unselectedListHeader}
          selectedList={selectedList}
          unselectedList={unselectedList}
          disabled={disabled}
          selectedListAction={selectedListAction}
          unselectedListAction={unselectedListAction}
        />
      )}
      <Box display="flex" mt={4} justifyContent="flex-end">
        <ButtonGroup align="right" flex={1}>
          {onCancel && (
            <Button
              color="primary"
              onClick={onCancel}
              data-qa="dual-list-container-save-btn"
            >
              {!!cancelButtonText ? cancelButtonText : t("closeBtn")}
            </Button>
          )}
          <LoadingButton
            pending={isSaving}
            disabled={disabled}
            color="primary"
            variant="contained"
            onClick={handleSave}
            data-qa="dual-list-container-save-btn"
          >
            {!!saveButtonText ? saveButtonText : t("saveBtn")}
          </LoadingButton>
        </ButtonGroup>
      </Box>
    </Box>
  );
};

export default DualListContainer;

DualListContainer.propTypes = {
  initialSelectedItems: PropTypes.array,
  initialUnselectedItems: PropTypes.array,
  selectedListHeader: PropTypes.string,
  unselectedListHeader: PropTypes.string,
  filters: PropTypes.node,
  filterUnselected: PropTypes.func,
  filterSelected: PropTypes.func,
  rowHeight: PropTypes.oneOfType([PropTypes.func, PropTypes.number]),
  getListItemDetails: PropTypes.func,
  onSave: PropTypes.func,
  isLoadingItems: PropTypes.bool,
  saveButtonText: PropTypes.string,
  disabled: PropTypes.bool
};

DualListContainer.defaultProps = {
  rowHeight: 89,
  estimatedItemSize: 89
};
